import type { ReactNode } from "react";
import { ErrorActions } from "./error-actions";
import { ErrorId } from "./error-id";

interface ErrorContentProps {
  code: string;
  title: string;
  description: string;
  /** Dev-only stack trace — rendered only when import.meta.env.DEV is true */
  devStack?: string;
  /** Optional extra content (e.g. custom actions) to replace default ErrorActions */
  children?: ReactNode;
}

// Shared layout for 404/403/500 content in the right panel.
export function ErrorContent({ code, title, description, devStack, children }: ErrorContentProps) {
  return (
    <div className="flex flex-col items-center gap-6 text-center">
      {/* Large error code */}
      <div
        className="text-8xl font-black tracking-tight"
        style={{ color: "var(--primary)" }}
        aria-hidden
      >
        {code}
      </div>

      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-[var(--foreground)]">{title}</h2>
        <p className="max-w-sm text-sm text-[var(--muted-foreground)]">{description}</p>
      </div>

      {children ?? <ErrorActions />}

      <ErrorId />

      {/* Stack trace visible only in dev builds for debugging */}
      {import.meta.env.DEV && devStack && (
        <details className="w-full text-left">
          <summary className="cursor-pointer text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)]">
            Stack trace (dev only)
          </summary>
          <pre className="mt-2 overflow-auto rounded-lg bg-[var(--secondary)] p-3 text-xs text-[var(--foreground)]">
            {devStack}
          </pre>
        </details>
      )}
    </div>
  );
}
